import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { HeaderComponent } from '../../shared/header/header.component';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { UserService } from '../../../services/user/user.service';
import { PasswordService } from '../../../services/password/password.service';
import { QueueConfigService } from '../../../services/queue-config/queue-config.service';
import { TimerConfigService } from '../../../services/timer-config/timer-config.service';
import { PasswordDTO } from '../../../interfaces/password-dto';
import { environment } from '../../../../environments/environment';
import {
  HubConnection,
  HubConnectionBuilder,
  HttpTransportType,
} from '@microsoft/signalr';
import { interval, Subscription } from 'rxjs';
import { fatorPesoEscalonado } from '../../../utils/peso-escalonado.util';
import { nomeExibicao } from '../../../utils/nome-exibicao.util';
import { GrupoConfig, GRUPOS_DEF } from '../../../constants/grupos-def.const';
import {
  FilaConfig, parseFilaConfig, pesoGrupo,
  qualificaGrupo, calcScoreFila, esperaMs, EscapeConfig, ESCAPE_PADRAO, estourouEspera,
} from '../../../utils/fila-config.util';

const LS_ORDER_KEY = 'gerenciar-fila-order';

@Component({
  selector: 'app-gerenciar-fila',
  standalone: true,
  imports: [CommonModule, HeaderComponent, RouterLink],
  templateUrl: './gerenciar-fila.component.html',
})
export class GerenciarFilaComponent implements OnInit, OnDestroy {
  passwords:    PasswordDTO[] = [];
  isLoading     = false;
  unitCode!:    number;
  userProfile!: string;
  connectionHub: HubConnection | undefined;
  elapsedTimes: { [id: number]: string } = {};
  timerSub:     Subscription | undefined;

  readonly AVG_SERVICE_MIN = 12;

  private _gruposCache: { [key: string]: PasswordDTO[] } = {};

  groupOrder:     string[]                  = GRUPOS_DEF.map(g => g.key);
  customWeights:  { [key: string]: number } = {};
  escapeConfig:   EscapeConfig              = { ...ESCAPE_PADRAO };
  showConfig      = false;
  autoCallEnabled = true;
  savingAutoCall  = false;
  autoCallError   = false;
  openSections: { [key: string]: boolean } = {};

  get grupos(): GrupoConfig[] {
    return this.groupOrder.map(k => GRUPOS_DEF.find(g => g.key === k)!);
  }

  constructor(
    private localStorageService: LocalStorageService,
    private userService: UserService,
    private passwordService: PasswordService,
    private queueConfigService: QueueConfigService,
    private timerConfigService: TimerConfigService,
    private cdr: ChangeDetectorRef,
  ) {
    this.unitCode    = this.localStorageService.get('currentUnit');
    this.userProfile = this.localStorageService.get('profile');
    GRUPOS_DEF.forEach(g => this.openSections[g.key] = true);
  }

  async ngOnInit() {
    await Promise.all([this.loadOrder(), this.loadAutoCall()]);
    await this.loadPasswords();
    this.connectSignalR();
    this.startTimers();
  }

  private async loadAutoCall() {
    try {
      const config = await this.timerConfigService.getTimerConfig(this.unitCode);
      this.autoCallEnabled = config.autoCallEnabled !== false;
    } catch {}
  }

  async toggleAutoCall() {
    this.savingAutoCall = true;
    this.autoCallError = false;
    this.autoCallEnabled = !this.autoCallEnabled;
    try {
      // getTimerConfig agora propaga falha em vez de devolver defaults em silencio - se cair
      // aqui, o catch reverte o toggle sem chamar saveTimerConfig (senao gravaria a unidade
      // inteira - priorizar e niveis de particular inclusive - por cima com defaults).
      const config = await this.timerConfigService.getTimerConfig(this.unitCode);
      const username: string = this.localStorageService.get('login') ?? 'OPERADOR';
      await this.timerConfigService.saveTimerConfig(
        this.unitCode, config.timerSeconds, config.autoPauseMinutes, this.autoCallEnabled, username,
        config.hrInicioPriorizar, config.hrFimPriorizar, config.nrSeqComputadorPriorizar, config.particularLevels
      );
    } catch (error) {
      console.error('Erro ao alternar chamada automática:', error);
      this.autoCallEnabled = !this.autoCallEnabled;
      this.autoCallError = true;
      setTimeout(() => (this.autoCallError = false), 4000);
    } finally {
      this.savingAutoCall = false;
    }
  }

  ngOnDestroy() {
    this.timerSub?.unsubscribe();
    try { this.connectionHub?.stop(); } catch {}
  }

  // ── Configuração de ordem e pesos ────────────────────────────────────────

  private async loadOrder() {
    try {
      const remote = await this.queueConfigService.getGroupOrder(this.unitCode);
      if (remote) {
        const parsed  = parseFilaConfig(remote);
        const valid   = parsed.order.filter(k => GRUPOS_DEF.some(g => g.key === k));
        const missing = GRUPOS_DEF.map(g => g.key).filter(k => !valid.includes(k));
        if (missing.length === 0) {
          this.groupOrder    = valid;
          this.customWeights = parsed.weights;
          this.escapeConfig  = parsed.escape;
          localStorage.setItem(LS_ORDER_KEY, remote);
          return;
        }
        this.groupOrder    = GRUPOS_DEF.map(g => g.key);
        this.customWeights = {};
        this.escapeConfig  = parsed.escape;   // order inválida reseta só a ordem; preserva o escape da unidade
        this.saveOrder();
        return;
      }
    } catch {}

    try {
      const saved = localStorage.getItem(LS_ORDER_KEY);
      if (saved) {
        const parsed  = parseFilaConfig(saved);
        const valid   = parsed.order.filter(k => GRUPOS_DEF.some(g => g.key === k));
        const missing = GRUPOS_DEF.map(g => g.key).filter(k => !valid.includes(k));
        if (missing.length === 0) {
          this.groupOrder    = valid;
          this.customWeights = parsed.weights;
          this.escapeConfig  = parsed.escape;
          return;
        }
        this.groupOrder    = GRUPOS_DEF.map(g => g.key);
        this.customWeights = {};
        this.escapeConfig  = parsed.escape;   // order inválida reseta só a ordem; preserva o escape da unidade
        this.saveOrder();
      }
    } catch {}
  }

  private saveOrder() {
    const username: string = this.localStorageService.get('login') ?? 'OPERADOR';
    const hasCustom = Object.keys(this.customWeights).length > 0;
    const hasEscape = this.escapeConfig.enabled;
    let configJson: string;
    if (hasCustom || hasEscape) {
      const body: any = { order: this.groupOrder };
      if (hasCustom) body.weights = this.customWeights;
      if (hasEscape) body.escape = this.escapeConfig;
      configJson = JSON.stringify(body);
    } else {
      configJson = JSON.stringify(this.groupOrder);
    }
    localStorage.setItem(LS_ORDER_KEY, configJson);
    this.queueConfigService.saveGroupOrder(
      this.unitCode, this.groupOrder, username,
      hasCustom ? this.customWeights : undefined,
      hasEscape ? this.escapeConfig : undefined,
    );
  }

  moveUp(index: number) {
    if (index === 0) return;
    [this.groupOrder[index - 1], this.groupOrder[index]] = [this.groupOrder[index], this.groupOrder[index - 1]];
    this.groupOrder = [...this.groupOrder];
    this.saveOrder();
    this.rebuildGroups();
  }

  moveDown(index: number) {
    if (index === this.groupOrder.length - 1) return;
    [this.groupOrder[index], this.groupOrder[index + 1]] = [this.groupOrder[index + 1], this.groupOrder[index]];
    this.groupOrder = [...this.groupOrder];
    this.saveOrder();
    this.rebuildGroups();
  }

  resetOrder() {
    this.groupOrder    = GRUPOS_DEF.map(g => g.key);
    this.customWeights = {};
    this.saveOrder();
    this.rebuildGroups();
  }

  setWeight(key: string, event: Event) {
    const v = +(event.target as HTMLInputElement).value;
    if (v > 0) {
      this.customWeights = { ...this.customWeights, [key]: v };
      this.saveOrder();
      this.rebuildGroups();
    }
  }

  // ── Dados ────────────────────────────────────────────────────────────────

  async loadPasswords() {
    this.isLoading = true;
    try {
      const { data } = await this.passwordService.getPasswordQueue(this.unitCode);
      this.passwords = (data as PasswordDTO[]).map(p => {
        p.priority = p.passwordType.toUpperCase().includes('PRIORIDADE');
        return p;
      });
      this.rebuildGroups();
    } catch {}
    finally { this.isLoading = false; }
  }

  connectSignalR() {
    const options = {
      skipNegotiation: true,
      transport: HttpTransportType.WebSockets,
      accessTokenFactory: () => this.userService.getTokenAuth(),
    };
    this.connectionHub = new HubConnectionBuilder()
      .withUrl(environment.hubUrl, options)
      .withAutomaticReconnect()
      .build();

    this.connectionHub.onreconnected(async () => {
      this.connectionHub!.invoke('Subscription', String(this.unitCode));
      await this.loadPasswords();
    });

    this.connectionHub.start()
      .then(() => this.connectionHub!.invoke('Subscription', String(this.unitCode)))
      .catch(() => {});

    this.connectionHub.off('Password');
    this.connectionHub.off('AttPassword');
    this.connectionHub.off('RemovePassword');

    this.connectionHub.on('Password', (result: any) => {
      if (!result) return;
      const { data } = JSON.parse(result);
      if (!data) return;
      const p = data[0] as PasswordDTO;
      if (!this.passwords.some(x => x.id === p.id)) {
        p.priority = p.passwordType.toUpperCase().includes('PRIORIDADE');
        this.passwords.push(p);
        this.rebuildGroups();
        this.cdr.detectChanges();
      }
    });

    this.connectionHub.on('AttPassword', (result: any) => {
      if (!result) return;
      const { data } = JSON.parse(result);
      if (!data) return;
      const index = this.passwords.findIndex(p => p.id === data[0].id);
      if (index !== -1) {
        const agendaHoje = data[0].agendaHoje || this.passwords[index].agendaHoje;
        this.passwords[index] = { ...this.passwords[index], ...data[0], agendaHoje };
        this.rebuildGroups();
        this.cdr.detectChanges();
      }
    });

    this.connectionHub.on('RemovePassword', (result: any) => {
      if (!result) return;
      const { data } = JSON.parse(result);
      if (!data || !data.length) return;
      this.passwords = this.passwords.filter(p => p.id !== data[0].id);
      this.rebuildGroups();
      this.cdr.detectChanges();
    });
  }

  startTimers() {
    this.timerSub?.unsubscribe();
    let tickCount = 0;
    this.timerSub = interval(1000).subscribe(() => {
      const now = new Date().getTime();
      this.passwords.forEach(p => {
        const diff = now - new Date(p.passwordGenerationDate).getTime();
        const min  = Math.floor(diff / 60000);
        const sec  = Math.floor((diff % 60000) / 1000);
        this.elapsedTimes[p.id] = `${min}m ${sec.toString().padStart(2, '0')}s`;
      });
      tickCount++;
      if (tickCount % 30 === 0) {
        this.loadPasswords();
      }
    });
  }

  // ── Cache de grupos ──────────────────────────────────────────────────────

  private rebuildGroups() {
    const map: { [key: string]: PasswordDTO[] } = {};
    GRUPOS_DEF.forEach(g => { map[g.key] = []; });

    for (const p of this.passwords) {
      if (this.isExpress(p)) continue;
      const key = this.assignGroup(p);
      if (key && map[key]) map[key].push(p);
    }

    Object.keys(map).forEach(key => {
      map[key].sort((a, b) => this.calcScore(b) - this.calcScore(a));
    });

    this._gruposCache = map;
  }

  // ── Classificação exclusiva ──────────────────────────────────────────────

  private get filaConfig(): FilaConfig {
    return { order: this.groupOrder, weights: this.customWeights, escape: this.escapeConfig };
  }

  qualifica(p: PasswordDTO, key: string): boolean {
    return qualificaGrupo(p, key, Date.now());
  }

  private assignGroup(p: PasswordDTO): string {
    for (const key of this.groupOrder) {
      if (this.qualifica(p, key)) return key;
    }
    return '';
  }

  calcScore(p: PasswordDTO): number {
    return calcScoreFila(p, this.filaConfig, Date.now());
  }

  estourou(p: PasswordDTO): boolean {
    return estourouEspera(p, this.filaConfig, Date.now());
  }

  getQualifyingGroups(p: PasswordDTO): { config: GrupoConfig; weight: number; fator: number }[] {
    const cfg = this.filaConfig;
    const now = Date.now();
    return cfg.order
      .filter(k => qualificaGrupo(p, k, now))
      .map(k => {
        const fator = fatorPesoEscalonado(k, p.planType, esperaMs(p, now));
        return { config: this.grupoConfig(k), weight: pesoGrupo(k, cfg) * fator, fator };
      });
  }

  groupWeight(key: string): number {
    return pesoGrupo(key, this.filaConfig);
  }

  estimatedWaitMin(key: string): number {
    const group = this.getGrupo(key);
    if (!group.length) return 0;
    const avg = this.mediaTempEsperaMin > 0 ? this.mediaTempEsperaMin : this.AVG_SERVICE_MIN;
    const topScore = this.calcScore(group[0]);
    const ahead = this.passwords.filter(p => !this.isExpress(p) && this.calcScore(p) > topScore).length;
    return Math.round(ahead * avg);
  }

  getGrupo(key: string): PasswordDTO[] {
    return this._gruposCache[key] ?? [];
  }

  // ── Métricas ─────────────────────────────────────────────────────────────

  get totalAtivos(): number {
    return this.passwords.filter(p => !this.isExpress(p) && this.isHoje(p)).length;
  }

  get totalAguardando(): number {
    return this.passwords.filter(p => !this.isExpress(p) && !p.calledMachine && this.isHoje(p)).length;
  }

  private get aguardandoHoje(): PasswordDTO[] {
    return this.passwords.filter(p => !this.isExpress(p) && !p.calledMachine && this.isHoje(p));
  }

  get mediaTempEsperaMin(): number {
    const lista = this.aguardandoHoje;
    if (!lista.length) return 0;
    return Math.round(lista.reduce((sum, p) => sum + this.waitMs(p), 0) / lista.length / 60000);
  }

  get maiorEsperaMin(): number {
    const lista = this.aguardandoHoje;
    if (!lista.length) return 0;
    return Math.round(Math.max(...lista.map(p => this.waitMs(p))) / 60000);
  }

  countGrupo(key: string): number {
    return (this._gruposCache[key] ?? []).length;
  }

  barWidthPct(key: string): number {
    if (!this.totalAtivos) return 0;
    return Math.round((this.countGrupo(key) / this.totalAtivos) * 100);
  }

  grupoConfig(key: string): GrupoConfig {
    return GRUPOS_DEF.find(g => g.key === key)!;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  isExpress(p: PasswordDTO): boolean {
    return p.verifyLetter === 'EX';
  }

  private isHoje(p: PasswordDTO): boolean {
    const hoje = new Date();
    const d    = new Date(p.passwordGenerationDate);
    return d.getFullYear() === hoje.getFullYear() &&
           d.getMonth()     === hoje.getMonth()    &&
           d.getDate()      === hoje.getDate();
  }

  waitMs(p: PasswordDTO): number {
    return Date.now() - new Date(p.passwordGenerationDate).getTime();
  }

  toggleSection(key: string) {
    this.openSections[key] = !this.openSections[key];
  }

  readonly nomeExibicao = nomeExibicao;

  shortName(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 2) return name;
    return `${parts[0]} ${parts[parts.length - 1]}`;
  }

  scheduledLabel(p: PasswordDTO): string {
    if (p.hasSchedule === 'N' || !p.scheduledTo) return '—';
    return new Date(p.scheduledTo).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  atrasoLabel(p: PasswordDTO): string {
    if (p.hasSchedule === 'N' || !p.scheduledTo) return '';
    const diff = Math.floor((new Date(p.passwordGenerationDate).getTime() - new Date(p.scheduledTo).getTime()) / 60000);
    return diff > 0 ? `+${diff} min` : '';
  }

  delayColor(p: PasswordDTO): 'green' | 'yellow' | 'red' {
    const min = this.waitMs(p) / 60000;
    if (min < 15) return 'green';
    if (min < 30) return 'yellow';
    return 'red';
  }
}
